import { format, parseISO } from "date-fns";
import { fromZonedTime, formatInTimeZone } from "date-fns-tz";
import dayjs from "dayjs";
import Cookies from "js-cookie";

export const formatDate = (dob) => {
  if (!dob) return "";
  const date = new Date(dob);
  return isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
};

export const parseISODateTime = (isoString, timezone) => {
  if (!isoString) return ["", ""];

  try {
    const institutionTZ = timezone || "Asia/Kuala_Lumpur";
    const parsedDate = parseISO(isoString);
    const formattedDate = formatInTimeZone(parsedDate, institutionTZ, "yyyy-MM-dd");
    const formattedTime = formatInTimeZone(parsedDate, institutionTZ, "HH:mm");
    return [formattedDate, formattedTime];
  } catch (error) {
    const dateTime = dayjs(isoString);
    return [dateTime.format("YYYY-MM-DD"), dateTime.format("HH:mm")];
  }
};

export const formatDateTime = (isoString, timezone) => {
  if (!isoString) return { formattedDate: "-", formattedTime: "-" };

  try {
    const institutionTZ = timezone || "Asia/Kuala_Lumpur";
    const instant = parseISO(isoString);
    if (isNaN(instant.getTime())) {
      throw new Error("Invalid date");
    }

    const formattedDate = formatInTimeZone(
      instant,
      institutionTZ,
      "do MMMM yyyy",
    );

    const formattedTime = formatInTimeZone(
      instant,
      institutionTZ,
      "hh:mm a",
    );

    return { formattedDate, formattedTime };
  } catch (error) {
    console.error("Invalid ISO date:", isoString, error);
    return { formattedDate: "-", formattedTime: "-" };
  }
};

export const formatToISODateTime = (scheduledDate, scheduledTime, timezone) => {
  if (!scheduledDate || !scheduledTime) return null;

  try {
    const institutionTZ = timezone || "Asia/Kuala_Lumpur";

    const timePart =
      scheduledTime.length === 5 ? `${scheduledTime}:00` : scheduledTime;
    const localDateTimeStr = `${scheduledDate}T${timePart}`;

    // Wall clock in institution TZ → UTC (do not build Date with local browser getters)
    const utcDate = fromZonedTime(localDateTimeStr, institutionTZ);

    if (isNaN(utcDate.getTime())) {
      throw new Error("Invalid date or time format.");
    }

    return utcDate.toISOString();
  } catch (error) {
    console.error("Invalid date or time format:", error);
    return null;
  }
};

/** True if scheduled date+time (interpreted in institution timezone) is strictly after now (UTC). */
export const isScheduledDateTimeInFuture = (
  scheduledDate,
  scheduledTime,
  timezone,
) => {
  const iso = formatToISODateTime(scheduledDate, scheduledTime, timezone);
  if (!iso) return false;
  return new Date(iso) > new Date();
};

export const formatTimeWithTimezone = (timeString, timezoneOffset = 8) => {
  if (!timeString) return "";

  try {
    const [hours, minutes] = timeString.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) throw new Error("Invalid time");

    const now = new Date();
    const time = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      hours,
      minutes,
      0,
    );

    return `${format(time, "hh:mm:ss a")} GMT${timezoneOffset >= 0 ? "+" : ""}${timezoneOffset}`;
  } catch (err) {
    console.error("Failed to format time:", err);
    return "";
  }
};

export const formatWithOrdinal = (date) => {
  const day = date.getDate();
  const month = date.toLocaleString("default", { month: "long" });
  const year = date.getFullYear();

  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
          ? "rd"
          : "th";

  return `${day}${suffix} ${month} ${year}`;
};

export const getTomorrow = () => {
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  return tomorrow;
};

export const formatTo12HourTime = (timeString) => {
  if (!timeString) return "";

  try {
    const [hours, minutes] = timeString.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) throw new Error("Invalid time");

    const date = new Date();
    date.setHours(hours, minutes, 0);

    return format(date, "hh:mm a");
  } catch (err) {
    console.error("Failed to format 12-hour time:", err);
    return "";
  }
};

export const formatToYYYYMMDD = (date) => {
  if (!date) return "";

  const d = new Date(date);
  if (isNaN(d.getTime())) return "";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const formatIsoDateToDDMMYYYY = (isoDate) => {
  if (!isoDate) return "";

  const match = String(isoDate).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return String(isoDate);

  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
};

export const formatETATime = (etaString) => {
  if (!etaString) return { formattedDate: "-", formattedTime: "-" };

  try {
    // Handle the format "3:19:08 AM" or similar time strings
    const timeMatch = etaString.match(/(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM)/i);

    if (timeMatch) {
      const [, hours, minutes, seconds, period] = timeMatch;
      let hour24 = parseInt(hours, 10);

      // Convert to 24-hour format
      if (period.toUpperCase() === "PM" && hour24 !== 12) {
        hour24 += 12;
      } else if (period.toUpperCase() === "AM" && hour24 === 12) {
        hour24 = 0;
      }

      // Create a date object for today with the parsed time
      const today = new Date();
      const etaTime = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        hour24,
        parseInt(minutes, 10),
        parseInt(seconds, 10),
      );

      const formattedDate = format(etaTime, "do MMMM yyyy");
      const formattedTime = format(etaTime, "hh:mm a");

      return { formattedDate, formattedTime };
    }

    // If it doesn't match the expected format, try to parse as ISO string
    const date = parseISO(etaString);
    const formattedDate = format(date, "do MMMM yyyy");
    const formattedTime = format(date, "hh:mm a");

    return { formattedDate, formattedTime };
  } catch (error) {
    console.error("Invalid ETA time format:", etaString, error);
    return { formattedDate: "-", formattedTime: "-" };
  }
};

export const getUTCOffset = (tz) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    timeZoneName: "shortOffset",
  }).formatToParts(new Date());

  return (
    parts.find((p) => p.type === "timeZoneName")?.value.replace("GMT", "UTC") ||
    "UTC"
  );
};
